// Knowledge nuggets — one slide, many decks (TODO §17).
//
// A nugget is the first object in this app that OUTLIVES a deck. Everything else in storage
// is deck-scoped: `griddDocs` and `assets` are queried by their owner, `deleteDeckCascade`
// deletes by it, and `remapBundle`'s stated rule is that a copied deck may share no id with
// its source — because two decks pointing at one `griddDocId` are one body aliased twice.
// **For a nugget that aliasing is the feature**: the body exists once, under the nugget's
// own owner key (`nuggetOwner`, §17.1.3), and every deck that links the nugget points at it.
//
// Phase 1 (§17.0–§17.7) is the local shelf. `origin` is here for phase 2's company server
// (§17.8) and is `"local"` for every record this build writes.

import type { SlideData } from "@/types/document";

// §17.2.1 EXACTLY the fields a link mirrors between a deck's slide and the nugget. Written
// down here, beside the record, because every future `SlideData` field has to choose a side
// and a list buried in a function body is a list nobody reads before adding one.
//
// Mirrored, and why: `title`/`subtitle`/`sticker`/`stickerTone`/`source` are the slide's
// words, `layout`/`contents`/`splitRatio`/`corners` are its shape. All nine are what
// "the same slide in five decks" means.
//
// **Not** mirrored, and why:
//   • `id` — slide ids are deck-local: they key selection, DOM ids and scroll targets.
//   • `archived` — deleting a slide from one deck must not delete it from four others.
//   • `level` — §18's pyramid level is a claim about THIS deck's argument, not about the
//     slide: the same nugget is a storyline claim in one deck and a backup in another.
//     (§17.2.1 predates §18 and does not name it; this is the reading that makes both work.)
//   • `link` itself — a nugget does not link to a nugget.
export const NUGGET_MIRRORED_FIELDS = [
  "title",
  "subtitle",
  "sticker",
  "stickerTone",
  "source",
  "layout",
  "contents",
  "splitRatio",
  "corners",
] as const;

export type NuggetMirroredField = (typeof NUGGET_MIRRORED_FIELDS)[number];

// The slide as the nugget holds it — the mirrored fields and nothing else. `id` rides along
// because `SlideData` requires one and a materialized slide needs something to be keyed by;
// it is regenerated on every insert and is never the id of any deck's slide.
export type NuggetSlide = Pick<SlideData, NuggetMirroredField | "id">;

// §17.1.1 The canonical record. `rev` is a monotonic counter owned by the storage layer,
// exactly like `DeckMeta.rev` (§12.5.4) — never incremented by a reducer, never trusted from
// a caller.
export interface NuggetRecord {
  nuggetId: string;
  slide: NuggetSlide;
  rev: number;
  createdAt: string; // ISO
  updatedAt: string; // ISO
  // §17.1.1 `"local"` in phase 1; `"server:<host>"` once §17.8's shelf exists.
  origin: string;
}

// §17.1.2 The listing row, for the same reason `deckIndex` exists (§12.3): the panel must
// list fifty nuggets without deserializing fifty slides and their grid bodies.
export interface NuggetIndexEntry {
  nuggetId: string;
  // §17.4.6 The nugget's name IS its slide's title — there is no second name field, and
  // renaming the slide in any deck renames the nugget everywhere, because it is the same
  // slide. Empty when the slide has no title; the panel says "Untitled nugget".
  name: string;
  updatedAt: string;
  // Captured off the deck's own slide thumbnail (§17.4.5) rather than by a second capturer.
  thumbDataUrl?: string;
}

// §17.1.4 What a deck's slide stores to say "this is that nugget". `rev` is the revision
// this deck's materialized copy was last brought into step with — the hydration-path
// reconcile (§17.2.5) compares it against the nugget's own.
export interface NuggetLink {
  nuggetId: string;
  rev: number;
}
